import React, { useState, useRef, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { getConversionsFromFormat } from '../../data/conversions';
import { ImageFormat } from '../../services/imageService';
import styles from './NavDropdown.module.css';

interface NavDropdownProps {
  active?: boolean;
  onClick?: () => void;
  className?: string;
}

const FORMAT_LABELS: Partial<Record<ImageFormat, string>> = {
  jpeg: 'JPG', png: 'PNG', webp: 'WebP', avif: 'AVIF',
  gif: 'GIF', bmp: 'BMP', ico: 'ICO', svg: 'SVG',
};

const SOURCE_FORMATS: ImageFormat[] = ['png', 'jpeg', 'webp', 'svg', 'gif', 'bmp'];

const NavDropdown: React.FC<NavDropdownProps> = ({ active, onClick, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { lang } = useParams<{ lang: string }>();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const triggerClasses = [
    'text-gray-700 font-semibold px-6 py-2 rounded-[1rem]',
    'flex items-center gap-1 transition-all duration-200',
    'hover:bg-primary-50 hover:text-primary cursor-pointer',
    active ? 'bg-primary-50 text-primary' : '',
    className,
  ].join(' ');

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        className={triggerClasses}
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        Convert
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 pt-2 z-50">
          <div className={`${styles.panel} bg-white rounded-[1rem] shadow-lg border border-gray-200 py-2 min-w-[260px]`}>
            <div className={`${styles.scroll} max-h-[400px] overflow-y-auto`}>
              {SOURCE_FORMATS.map(sourceFormat => {
                const list = getConversionsFromFormat(sourceFormat);
                if (list.length === 0) return null;
                return (
                  <div key={sourceFormat}>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-4 pt-3 pb-1 mb-0">
                      From {FORMAT_LABELS[sourceFormat] ?? sourceFormat.toUpperCase()}
                    </p>
                    {list.map(conv => (
                      <Link
                        key={conv.slug}
                        to={`/${lang || 'en'}/convert/${conv.slug}`}
                        className="block px-4 py-1.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary mx-2 rounded-lg transition-colors"
                        onClick={() => {
                          setIsOpen(false);
                          onClick?.();
                        }}
                      >
                        {FORMAT_LABELS[conv.from] ?? conv.from.toUpperCase()} → {FORMAT_LABELS[conv.to] ?? conv.to.toUpperCase()}
                      </Link>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavDropdown;
